import dayjs from "dayjs"
import type { ReactElement } from "react"
import type { Control, FieldPath, FieldValues } from "react-hook-form"
import { useController } from "react-hook-form"

import {
  type AerosCustomDatePickerProps,
  AerosDatePickerWithCustomActions,
  type AerosDateValue,
  DateSpecialValue,
} from "../AerosDatePicker/AerosDatePickerWithCustomActions"
import CustomDatePickerLayout from "../AerosDatePicker/CustomDatePickerLayout"

import { DATE_FORMAT } from "@/constants/formats"

export interface FormDatePickerWithCustomActionsProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends Omit<AerosCustomDatePickerProps, "name" | "defaultValue" | "onChange" | "value" | "slots" | "slotProps"> {
  control: Control<TFieldValues>
  name: TName
  helperText?: string
  allowAsap?: boolean
  allowTbd?: boolean
  onChange?: (value: AerosDateValue) => void
  // Fires only when the ASAP action is clicked — distinct from onChange, which also fires for a
  // plain date pick. Lets a consumer propagate ASAP onto a sibling field.
  onAsapSelect?: () => void
  // Opt-in for dense layouts (e.g. a table-like row of many small fields) that surface a single
  // shared error banner elsewhere instead of per-field text — the red border still shows.
  hideErrorMessage?: boolean
}

export const FormDatePickerWithCustomActions = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  onChange,
  onAsapSelect,
  helperText,
  hideErrorMessage,
  allowAsap,
  allowTbd,
  ...props
}: FormDatePickerWithCustomActionsProps<TFieldValues, TName>): ReactElement => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const handleChange = (value: AerosDateValue) => {
    field.onChange(value)
    onChange?.(value)
  }

  const handleAsapSelect = () => {
    handleChange(DateSpecialValue.ASAP)
    onAsapSelect?.()
  }

  const handleAccept = () => {
    if (!field.value) {
      handleChange(dayjs().format(DATE_FORMAT.ISO_18601))
    }
  }

  return (
    <AerosDatePickerWithCustomActions
      {...field}
      {...props}
      error={!!error}
      helperText={hideErrorMessage ? helperText : (error?.message ?? helperText)}
      onChange={handleChange}
      slots={{ layout: CustomDatePickerLayout }}
      slotProps={{
        layout: {
          onAsap: allowAsap ? handleAsapSelect : undefined,
          onTbd: allowTbd ? () => handleChange(DateSpecialValue.TBD) : undefined,
          onAccept: handleAccept,
        },
      }}
    />
  )
}
